import { useEffect, useMemo, useState, type Dispatch, type SetStateAction } from 'react';
import { api, type GabulBlRow, type GabulMonthResult, type GabulMonthRow, type GabulSmsJob, type GabulSmsRow } from '../lib/api';
import { ErrorBox, money, MoneyInput } from '../components/DataTable';
import Modal from '../components/Modal';
import { canScreen, useMe } from '../lib/me';

/**
 * 회계 — 가불금·블랙리스트(10-23 · 구글시트 `가불금_블랙리스트_REALTIME` + 월별 사본 이관). 계산 규칙은 서버 `gabul.ts` 머리 주석.
 * 흐름: 정산부족분 확정 → 이 탭에서 그 달 계산 미리보기 → 「이 달 기록 굳히기」 → 추세·등급표·블랙리스트·SMS 명단.
 * SMS = 굳힌 달에서 「부족분 문자」 → 사람이 고른 차주에게 고른 날짜·시각에 예약(10-23 way). 시스템이 대상을 고르거나 스스로 보내지 않는다.
 */

const errMsg = (e: unknown) => (e instanceof Error ? e.message : String(e));
const nz = (v: unknown) => Math.round(Number(v ?? 0) || 0);
const GRADES = ['A', 'B', 'C', 'D', '증가'] as const;
const GRADE_TIP: Record<string, string> = {
  A: '잔존가치 < 0', B: '가불금(차주) 1천만 원 초과', C: '가불금(차주) 5백만 원 초과', D: '잔존가치 1.5천만 원 미만', 증가: '가불금(차량)이 전월보다 늘었다',
};
const GRADE_CLS: Record<string, string> = {
  A: 'bg-red-100 text-red-800', B: 'bg-orange-100 text-orange-800', C: 'bg-amber-100 text-amber-800', D: 'bg-sky-100 text-sky-800', 증가: 'bg-violet-100 text-violet-800',
};
const monLabel = (m: string) => `${m.slice(2, 4)}.${m.slice(5)}`;

async function saveXlsx(name: string, head: string[], rows: (string | number | null)[][]) {
  const XLSX = await import('xlsx');
  const wb = XLSX.utils.book_new();
  XLSX.utils.book_append_sheet(wb, XLSX.utils.aoa_to_sheet([head, ...rows]), name.slice(0, 31));
  XLSX.writeFile(wb, `${name}.xlsx`);
}

export function GabulTab() {
  const { me } = useMe();
  const canWrite = canScreen(me, 'accounting', 'write');
  const [months, setMonths] = useState<GabulMonthRow[]>([]);
  const [latest, setLatest] = useState<string | null>(null);
  const [mon, setMon] = useState<string>('');
  const [detail, setDetail] = useState<GabulMonthResult | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [busy, setBusy] = useState<string | null>(null);
  const [nonce, setNonce] = useState(0);
  const [range, setRange] = useState<24 | 0>(24);
  const [pricesOpen, setPricesOpen] = useState(false);
  const [smsOpen, setSmsOpen] = useState(false);

  useEffect(() => {
    api.listGabulMonths({}).then((r) => {
      setMonths(r.rows); setLatest(r.latestSalaryMon);
      setMon((m) => m || r.latestSalaryMon || r.rows.at(-1)?.mon || '');
    }).catch((e) => setError(errMsg(e)));
  }, [nonce]);

  useEffect(() => {
    if (!mon) return;
    let cancelled = false;
    setLoading(true); setError(null); setDetail(null);
    api.getGabulMonth({ mon })
      .then((r) => { if (!cancelled) setDetail(r); })
      .catch((e) => { if (!cancelled) setError(errMsg(e)); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [mon, nonce]);

  // 월 선택지 = 기록된 달 + 아직 안 굳힌 최근 명세 달.
  const monOptions = useMemo(() => {
    const s = new Set(months.map((m) => m.mon));
    if (latest) s.add(latest);
    return [...s].sort().reverse();
  }, [months, latest]);

  const bl: GabulBlRow[] = useMemo(() => {
    if (!detail) return [];
    if (detail.saved) return detail.bl.map((r) => ({
      ...r, residual: nz(r.residual), car_amt: nz(r.car_amt), owner_amt: nz(r.owner_amt), inst_remain: nz(r.inst_remain),
      est_price: nz(r.est_price), number_value: nz(r.number_value),
    }));
    return detail.lines.filter((l) => l.listed).map((l) => ({
      grade: l.grade!, owner_name: l.ownerName, car_no: l.carNo, grp_name: l.grpName, car_name: l.carName, car_year: l.carYear,
      residual: l.residual, car_amt: l.carAmt, owner_amt: l.ownerAmt, inst_remain: l.instRemain, est_price: l.estPrice, number_value: l.numberValue,
    }));
  }, [detail]);
  const counts = useMemo(() => Object.fromEntries(GRADES.map((g) => [g, bl.filter((r) => r.grade === g).length])), [bl]);
  const ownerCnt = detail ? detail.owners.length : 0;
  const prevTotal = useMemo(() => {
    const i = months.findIndex((m) => m.mon === mon);
    const before = (i >= 0 ? months.slice(0, i) : months.filter((m) => m.mon < mon)).at(-1);
    return before ? nz(before.total) : null;
  }, [months, mon]);

  const save = async () => {
    if (!detail || detail.saved && detail.source === 'sheet') return;
    const again = detail.saved ? '이미 굳힌 달입니다. 지금 시각 기준으로 다시 굳힐까요?' : `${mon} 을 지금 시각 기준으로 굳힐까요?\n(그 뒤 들어오는 입금·수정은 이 달 기록을 바꾸지 않습니다)`;
    if (!window.confirm(again)) return;
    setBusy('save');
    try {
      await api.saveGabulMonth({ mon });
      setNonce((x) => x + 1);
    } catch (e) { setError(errMsg(e)); } finally { setBusy(null); }
  };

  const exportSms = async () => {
    setBusy('sms');
    try {
      const r = await api.listGabulSms({ mon });
      await saveXlsx(`가불금_SMS_${mon}`, ['대상', '번호', 'Message', '가불금(차주)', '차인지급액'],
        r.rows.map((x) => [x.ownerName, x.phone, x.message, x.amt, x.net]));
    } catch (e) { setError(errMsg(e)); } finally { setBusy(null); }
  };
  const exportBl = () => saveXlsx(`블랙리스트_${mon}`,
    ['등급판별', '차주', '차량', '소속', '잔존가치', '가불금(차량)', '가불금(차주)', '할부잔액', '추정시세', '번호권리가치', '차종', '연식', '히스토리'],
    bl.map((r) => [r.grade, r.owner_name, r.car_no, r.grp_name, r.residual, r.car_amt, r.owner_amt, r.inst_remain, r.est_price, r.number_value, r.car_name, r.car_year, r.memo ?? null]));

  // 2019-04 이전 사본은 합계 칸이 없어 total 이 비어 있다 — 추세에서만 뺀다(등급표·블랙리스트는 그대로).
  const withTotal = months.filter((m) => m.total != null);
  const shown = range ? withTotal.slice(-range) : withTotal;
  const gradeMonths = months.slice(-13);

  return (
    <div className="space-y-3">
      <div className="flex flex-wrap items-center gap-2">
        <select value={mon} onChange={(e) => setMon(e.target.value)} className="field w-auto" aria-label="정산월">
          {monOptions.map((m) => <option key={m} value={m}>{m}{months.some((x) => x.mon === m) ? '' : ' (안 굳힘)'}</option>)}
        </select>
        {detail && (
          <span className={`rounded px-2 py-0.5 text-xs ${detail.saved ? 'bg-emerald-50 text-emerald-700' : 'bg-amber-50 text-amber-800'}`}
                title={detail.cutAt ? `재무추가를 ${detail.cutAt} 전 줄까지 셌습니다` : undefined}>
            {detail.saved ? (detail.source === 'sheet' ? '시트 사본에서 옮긴 기록' : `굳힌 기록 · ${detail.cutAt?.slice(0, 16)} 기준`) : '계산 미리보기 — 아직 안 굳힘'}
          </span>
        )}
        <div className="ml-auto flex flex-wrap gap-2">
          {canWrite && detail && !(detail.saved && detail.source === 'sheet') && (
            <button className="btn-primary" disabled={!!busy} onClick={save}>{busy === 'save' ? '굳히는 중…' : detail.saved ? '다시 굳히기' : '이 달 기록 굳히기'}</button>
          )}
          {detail?.saved && detail.source === 'web' && (
            <button className="btn" disabled={!!busy} onClick={() => setSmsOpen(true)}>부족분 문자</button>
          )}
          <button className="btn" disabled={!detail || !!busy} onClick={exportSms}>{busy === 'sms' ? '만드는 중…' : 'SMS 명단 엑셀'}</button>
          <button className="btn" disabled={!bl.length} onClick={exportBl}>블랙리스트 엑셀</button>
          <button className="btn" onClick={() => setPricesOpen(true)}>시세표·번호권리 단가</button>
        </div>
      </div>
      {error && <ErrorBox message={error} />}

      <div className="grid gap-3 lg:grid-cols-[minmax(0,3fr)_minmax(0,2fr)]">
        <div className="card p-3">
          <div className="mb-1 flex items-center gap-2">
            <h2 className="text-sm font-semibold">가불금 추세</h2>
            <span className="text-xs text-ink-soft">차주 이월잔액 마이너스 합</span>
            <div className="ml-auto flex gap-1 text-xs">
              {([24, 0] as const).map((r) => (
                <button key={r} onClick={() => setRange(r)} className={`rounded px-2 py-0.5 ${range === r ? 'bg-brand-600 font-semibold text-white shadow-sm' : 'text-ink hover:bg-brand-50'}`}>
                  {r ? '최근 2년' : `전체 (${withTotal.length}개월)`}
                </button>
              ))}
            </div>
          </div>
          <TrendChart rows={shown} active={mon} onPick={setMon} />
        </div>
        <div className="card p-3">
          <div className="mb-1 flex items-center gap-2">
            <h2 className="text-sm font-semibold">{mon} 요약</h2>
            {loading && <span className="text-xs text-ink-faint">불러오는 중…</span>}
          </div>
          {detail && (
            <dl className="grid grid-cols-2 gap-x-4 gap-y-1 text-sm">
              <dt className="text-ink-soft">가불금 잔액</dt><dd className="text-right font-semibold">{money(detail.total)}</dd>
              <dt className="text-ink-soft">전월 대비</dt>
              <dd className="text-right">{prevTotal === null ? '—' : <span className={detail.total - prevTotal > 0 ? 'text-red-600' : 'text-emerald-700'}>{detail.total - prevTotal > 0 ? '+' : ''}{money(detail.total - prevTotal)}</span>}</dd>
              <dt className="text-ink-soft">마이너스 차주(SMS 대상)</dt><dd className="text-right">{ownerCnt}명</dd>
              <dt className="text-ink-soft">블랙리스트</dt><dd className="text-right">{bl.length}대</dd>
            </dl>
          )}
          <div className="mt-2 flex flex-wrap gap-1">
            {GRADES.map((g) => <span key={g} title={GRADE_TIP[g]} className={`rounded px-2 py-0.5 text-xs ${GRADE_CLS[g]}`}>{g} {counts[g] ?? 0}</span>)}
          </div>
        </div>
      </div>

      <div className="card overflow-x-auto p-3">
        <h2 className="mb-1 text-sm font-semibold">등급별 인원 <span className="font-normal text-ink-soft">(직영·디샤인 제외)</span></h2>
        <table className="w-full min-w-[40rem] text-xs tabular-nums">
          <thead><tr className="text-ink-soft"><th className="px-1 py-1 text-left">구분</th>{gradeMonths.map((m) => <th key={m.mon} className={`px-1 py-1 text-right ${m.mon === mon ? 'text-brand-700' : ''}`}>{monLabel(m.mon)}</th>)}</tr></thead>
          <tbody>
            {GRADES.map((g) => {
              const key = ({ A: 'cnt_a', B: 'cnt_b', C: 'cnt_c', D: 'cnt_d', 증가: 'cnt_up' } as const)[g];
              return (
                <tr key={g} className="border-t border-line">
                  <td className="px-1 py-1" title={GRADE_TIP[g]}>{g}</td>
                  {gradeMonths.map((m) => <td key={m.mon} className="px-1 py-1 text-right">{m[key] ?? '—'}</td>)}
                </tr>
              );
            })}
            <tr className="border-t border-line font-semibold">
              <td className="px-1 py-1">계</td>
              {gradeMonths.map((m) => <td key={m.mon} className="px-1 py-1 text-right">{[m.cnt_a, m.cnt_b, m.cnt_c, m.cnt_d, m.cnt_up].every((v) => v == null) ? '—' : [m.cnt_a, m.cnt_b, m.cnt_c, m.cnt_d, m.cnt_up].reduce<number>((s, v) => s + (v ?? 0), 0)}</td>)}
            </tr>
          </tbody>
        </table>
      </div>

      <div className="card p-3">
        <h2 className="mb-1 text-sm font-semibold">{mon} 블랙리스트 <span className="font-normal text-ink-soft">{bl.length}대 · 잔존가치 = 추정시세 − 가불금(차량) − 할부잔액 + 번호권리가치</span></h2>
        <div className="max-h-[calc(100vh-14rem)] overflow-auto">
          <table className="w-full min-w-[60rem] text-xs tabular-nums [&_thead_th]:sticky [&_thead_th]:top-0 [&_thead_th]:bg-surface">
            <thead>
              <tr className="text-ink-soft">
                {['등급', '차주', '차량', '소속', '차종·연식', '잔존가치', '가불금(차량)', '가불금(차주)', '할부잔액', '추정시세', '번호권리'].map((h, i) => (
                  <th key={h} className={`px-1.5 py-1 ${i >= 5 ? 'text-right' : 'text-left'}`}>{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {bl.map((r, i) => (
                <tr key={i} className="border-t border-line" title={r.memo ?? undefined}>
                  <td className="px-1.5 py-1"><span className={`rounded px-1.5 ${GRADE_CLS[r.grade] ?? 'bg-canvas'}`} title={GRADE_TIP[r.grade]}>{r.grade}</span></td>
                  <td className="px-1.5 py-1">{r.owner_name}</td>
                  <td className="px-1.5 py-1">{r.car_no}</td>
                  <td className="px-1.5 py-1">{r.grp_name}</td>
                  <td className="px-1.5 py-1 text-ink-soft">{[r.car_name, r.car_year].filter(Boolean).join(' · ')}</td>
                  <td className={`px-1.5 py-1 text-right ${r.residual < 0 ? 'font-semibold text-red-600' : ''}`}>{money(r.residual)}</td>
                  <td className="px-1.5 py-1 text-right">{money(r.car_amt)}</td>
                  <td className="px-1.5 py-1 text-right">{money(r.owner_amt)}</td>
                  <td className="px-1.5 py-1 text-right">{money(r.inst_remain)}</td>
                  <td className="px-1.5 py-1 text-right">{money(r.est_price)}</td>
                  <td className="px-1.5 py-1 text-right">{money(r.number_value)}</td>
                </tr>
              ))}
              {!bl.length && !loading && <tr><td colSpan={11} className="px-1.5 py-4 text-center text-ink-faint">블랙리스트 대상이 없습니다.</td></tr>}
            </tbody>
          </table>
        </div>
      </div>
      {pricesOpen && <PricesModal canWrite={canWrite} onClose={() => { setPricesOpen(false); setNonce((x) => x + 1); }} />}
      {smsOpen && <SmsModal mon={mon} canWrite={canWrite} onClose={() => setSmsOpen(false)} />}
    </div>
  );
}

/** 단일 계열 꺾은선(범례 없음 — 제목이 계열 이름) · 점에 마우스 = 그 달 값 · 누르면 그 달로. */
function TrendChart({ rows, active, onPick }: { rows: GabulMonthRow[]; active: string; onPick: (m: string) => void }) {
  const [hover, setHover] = useState<number | null>(null);
  const W = 720, H = 220, L = 56, R = 12, T = 12, B = 26;
  if (!rows.length) return <div className="py-10 text-center text-xs text-ink-faint">기록이 없습니다.</div>;
  const vals = rows.map((r) => nz(r.total));
  // 눈금은 1·2·5 × 10^n 단위로 끊는다(9,381만 같은 눈금이 안 나오게).
  const raw = (Math.max(...vals) || 1) / 4;
  const pow = 10 ** Math.floor(Math.log10(raw));
  const unit = [1, 2, 5, 10].map((k) => k * pow).find((s) => s >= raw)!;
  const max = unit * 4;
  const x = (i: number) => L + (rows.length === 1 ? (W - L - R) / 2 : (i * (W - L - R)) / (rows.length - 1));
  const y = (v: number) => T + (1 - v / max) * (H - T - B);
  const ticks = [0, 0.25, 0.5, 0.75, 1].map((f) => max * f);
  const step = Math.max(1, Math.ceil(rows.length / 12));
  const eok = (v: number) => (v >= 1e8 ? `${(v / 1e8).toFixed(1)}억` : `${Math.round(v / 1e4).toLocaleString('ko-KR')}만`);
  const h = hover !== null ? rows[hover] : null;
  return (
    <div className="relative">
      <svg viewBox={`0 0 ${W} ${H}`} className="w-full" role="img" aria-label="가불금 잔액 추세" onMouseLeave={() => setHover(null)}>
        {ticks.map((t) => (
          <g key={t}>
            <line x1={L} x2={W - R} y1={y(t)} y2={y(t)} className="stroke-line" strokeWidth={1} />
            <text x={L - 6} y={y(t) + 3} textAnchor="end" className="fill-ink-soft text-[10px]">{eok(t)}</text>
          </g>
        ))}
        {rows.map((r, i) => (i % step === 0 || i === rows.length - 1) && (
          <text key={r.mon} x={x(i)} y={H - 8} textAnchor="middle" className="fill-ink-soft text-[10px]">{monLabel(r.mon)}</text>
        ))}
        <polyline fill="none" className="stroke-brand-600" strokeWidth={2} strokeLinejoin="round" points={vals.map((v, i) => `${x(i)},${y(v)}`).join(' ')} />
        {hover !== null && <line x1={x(hover)} x2={x(hover)} y1={T} y2={H - B} className="stroke-ink-faint" strokeDasharray="3 3" />}
        {rows.map((r, i) => (
          <g key={r.mon} onMouseEnter={() => setHover(i)} onClick={() => onPick(r.mon)} className="cursor-pointer">
            <rect x={x(i) - Math.max(6, (W - L - R) / rows.length / 2)} y={T} width={Math.max(12, (W - L - R) / rows.length)} height={H - T - B} fill="transparent" />
            {(r.mon === active || hover === i || rows.length <= 24) && (
              <circle cx={x(i)} cy={y(vals[i]!)} r={r.mon === active ? 5 : 4} className={r.mon === active ? 'fill-brand-600 stroke-surface' : 'fill-surface stroke-brand-600'} strokeWidth={2} />
            )}
          </g>
        ))}
      </svg>
      {h && (
        <div className="pointer-events-none absolute top-1 rounded border border-line bg-surface px-2 py-1 text-xs shadow"
             style={{ left: `${Math.min(80, (x(hover!) / W) * 100)}%` }}>
          <div className="font-semibold">{h.mon}</div>
          <div>{money(h.total)}원 · 차주 {h.owner_cnt ?? '—'}명</div>
          <div className="text-ink-soft">{h.source === 'sheet' ? '시트 사본' : '웹 굳힘'}</div>
        </div>
      )}
    </div>
  );
}

function PricesModal({ canWrite, onClose }: { canWrite: boolean; onClose: () => void }) {
  const [base, setBase] = useState<string | undefined>(undefined);
  const [data, setData] = useState<PricesData | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [q, setQ] = useState('');
  const [add, setAdd] = useState({ carName: '', carYear: '', price: '' });
  const [nonce, setNonce] = useState(0);
  useEffect(() => {
    api.listGabulPrices({ baseMon: base }).then(setData).catch((e) => setError(errMsg(e)));
  }, [base, nonce]);
  const run = async (p: Promise<unknown>) => { setError(null); try { await p; setNonce((x) => x + 1); } catch (e) { setError(errMsg(e)); } };
  const rows = (data?.rows ?? []).filter((r) => !q || r.car_name.includes(q));
  const cur = data?.base ?? '';
  return (
    <Modal width="max-w-4xl">
      <PricesBody {...{ canWrite, onClose, data, setData, error, q, setQ, add, setAdd, setBase, run, rows, cur }} />
    </Modal>
  );
}

type PricesData = Awaited<ReturnType<typeof api.listGabulPrices>>;
function PricesBody({ canWrite, onClose, data, setData, error, q, setQ, add, setAdd, setBase, run, rows, cur }: {
  canWrite: boolean; onClose: () => void; data: PricesData | null; setData: Dispatch<SetStateAction<PricesData | null>>;
  error: string | null; q: string; setQ: (v: string) => void; add: { carName: string; carYear: string; price: string };
  setAdd: (v: { carName: string; carYear: string; price: string }) => void; setBase: (v: string) => void;
  run: (p: Promise<unknown>) => Promise<void>; rows: PricesData['rows']; cur: string;
}) {
  return (
      <div className="card space-y-3 p-4">
        <div className="flex flex-wrap items-center gap-2">
          <h2 className="text-base font-semibold">차량 시세표 · 번호권리 단가</h2>
          <select className="field w-auto" value={cur} onChange={(e) => setBase(e.target.value)} aria-label="기준월">
            {(data?.bases ?? []).map((b) => <option key={b.base_mon} value={b.base_mon}>기준월 {b.base_mon} ({b.n}줄)</option>)}
          </select>
          {canWrite && cur && (
            <button className="btn" onClick={() => {
              const to = window.prompt('새 기준월(YYYY-MM) — 지금 표를 복사해 시작합니다', new Date(Date.now() + 9 * 3600_000).toISOString().slice(0, 7));
              if (to) void run(api.copyGabulPrices({ from: cur, to }).then(() => setBase(to)));
            }}>새 기준월로 복사</button>
          )}
          <button className="btn ml-auto" onClick={onClose}>닫기</button>
        </div>
        <p className="text-xs text-ink-soft">계산은 그 달 이전 가장 최근 기준월을 씁니다. 차종 이름은 기준정보 차량의 「차량명」과 글자까지 같아야 맞습니다(없으면 시세 0).</p>
        {error && <ErrorBox message={error} />}
        {!!data?.missing.length && (
          <div className="rounded bg-amber-50 p-2 text-xs text-amber-900">
            시세표에 없는 차종(최근 명세 차량): {data.missing.map((m) => `${m.car_name ?? '(빈 칸)'} ${m.n}대`).join(' · ')}
          </div>
        )}
        <div className="grid gap-3 md:grid-cols-[minmax(0,3fr)_minmax(0,2fr)]">
          <div>
            <input className="field mb-2" placeholder="차종 찾기" value={q} onChange={(e) => setQ(e.target.value)} />
            <div className="max-h-[55vh] overflow-auto">
              <table className="w-full text-xs tabular-nums [&_thead_th]:sticky [&_thead_th]:top-0 [&_thead_th]:bg-surface">
                <thead><tr className="text-ink-soft"><th className="px-1 py-1 text-left">차종</th><th className="px-1 py-1 text-right">연식</th><th className="px-1 py-1 text-right">시세(원)</th><th /></tr></thead>
                <tbody>
                  {rows.map((r) => (
                    <tr key={r.id} className="border-t border-line">
                      <td className="px-1 py-0.5">{r.car_name}</td>
                      <td className="px-1 py-0.5 text-right">{r.car_year}</td>
                      <td className="px-1 py-0.5 text-right">
                        {canWrite ? (
                          <MoneyInput className="field w-32 py-0.5 text-right" value={r.price}
                                      onChange={(v) => setData((d) => d && { ...d, rows: d.rows.map((x) => (x.id === r.id ? { ...x, price: v } : x)) })}
                                      onBlur={() => void run(api.saveGabulPrice({ baseMon: cur, carName: r.car_name, carYear: r.car_year, price: r.price }))} />
                        ) : money(r.price)}
                      </td>
                      <td className="px-1 py-0.5 text-right">
                        {canWrite && <button className="text-ink-faint hover:text-red-600" title="지우기" onClick={() => window.confirm(`${r.car_name} ${r.car_year} 을 지울까요?`) && void run(api.deleteGabulPrice({ id: r.id }))}>×</button>}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            {canWrite && cur && (
              <div className="mt-2 flex flex-wrap gap-1">
                <input className="field w-48" placeholder="차종(차량명)" value={add.carName} onChange={(e) => setAdd({ ...add, carName: e.target.value })} />
                <input className="field w-20" placeholder="연식" value={add.carYear} onChange={(e) => setAdd({ ...add, carYear: e.target.value })} />
                <MoneyInput className="field w-32" placeholder="시세(원)" value={add.price} onChange={(v) => setAdd({ ...add, price: v })} />
                <button className="btn" onClick={() => void run(api.saveGabulPrice({ baseMon: cur, carName: add.carName, carYear: Number(add.carYear), price: add.price }).then(() => setAdd({ carName: '', carYear: '', price: '' })))}>추가</button>
              </div>
            )}
          </div>
          <div>
            <h3 className="mb-1 text-sm font-semibold">번호권리 단가</h3>
            <p className="mb-2 text-xs text-ink-soft">번호권리가 차주 명의(회사 「동영…」이 아님)면 그 차 소속의 단가만큼 잔존가치에 더합니다.</p>
            <table className="w-full text-xs tabular-nums">
              <tbody>
                {(data?.numbers ?? []).map((r) => (
                  <tr key={r.grp_name} className="border-t border-line">
                    <td className="px-1 py-1">{r.grp_name}</td>
                    <td className="px-1 py-1 text-right">
                      {canWrite ? (
                        <MoneyInput className="field w-32 py-0.5 text-right" value={r.price}
                                    onChange={(v) => setData((d) => d && { ...d, numbers: d.numbers.map((x) => (x.grp_name === r.grp_name ? { ...x, price: v } : x)) })}
                                    onBlur={() => void run(api.saveGabulNumberPrice({ grpName: r.grp_name, price: r.price }))} />
                      ) : money(r.price)}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </div>
  );
}

// ── 부족분 문자 예약 ─────────────────────────────────────────────
const isMobile = (p: string | null) => /^01[0-9]{8,9}$/.test((p ?? '').replace(/[^0-9]/g, ''));
const LIVE = new Set(['대기', '보내는중', '보냄']);
const TIMES = Array.from({ length: 13 * 6 }, (_, i) => `${String(8 + Math.floor(i / 6)).padStart(2, '0')}:${i % 6}0`);
const JOB_CLS: Record<string, string> = {
  대기: 'bg-amber-50 text-amber-800', 보내는중: 'bg-sky-50 text-sky-800', 보냄: 'bg-emerald-50 text-emerald-700',
  실패: 'bg-red-50 text-red-700', 취소: 'bg-canvas text-ink-faint',
};
/** 기본 = 지금부터 20분 뒤 첫 10분 칸 · 20:50 을 넘으면 다음 날 08:00. */
function defaultSlot() {
  const t = new Date(Date.now() + 9 * 3600_000 + 20 * 60_000);
  let day = t.toISOString().slice(0, 10);
  let hm = `${t.toISOString().slice(11, 15)}0`;
  if (hm < '08:00') hm = '08:00';
  if (hm > '20:50') { day = new Date(t.getTime() + 24 * 3600_000).toISOString().slice(0, 10); hm = '08:00'; }
  return { day, hm };
}

function SmsModal({ mon, canWrite, onClose }: { mon: string; canWrite: boolean; onClose: () => void }) {
  const [rows, setRows] = useState<GabulSmsRow[] | null>(null);
  const [jobs, setJobs] = useState<GabulSmsJob[]>([]);
  const [pick, setPick] = useState<Set<number>>(new Set());
  const [jobPick, setJobPick] = useState<Set<number>>(new Set());
  const [slot, setSlot] = useState(defaultSlot);
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [nonce, setNonce] = useState(0);
  useEffect(() => {
    let cancelled = false;
    Promise.all([api.listGabulSms({ mon }), api.listGabulSmsJobs({ mon })]).then(([s, j]) => {
      if (cancelled) return;
      setRows(s.rows); setJobs(j.rows);
      const live = new Set(j.rows.filter((x) => LIVE.has(x.status)).map((x) => x.owner_id));
      setPick(new Set(s.rows.filter((r) => r.ownerId != null && r.message && isMobile(r.phone) && !live.has(r.ownerId)).map((r) => r.ownerId!)));
      setJobPick(new Set());
    }).catch((e) => !cancelled && setError(errMsg(e)));
    return () => { cancelled = true; };
  }, [mon, nonce]);
  return (
    <Modal width="max-w-5xl">
      <SmsBody {...{ mon, canWrite, onClose, rows, jobs, pick, setPick, jobPick, setJobPick, slot, setSlot, error, setError, notice, setNotice, busy, setBusy, reload: () => setNonce((x) => x + 1) }} />
    </Modal>
  );
}

function SmsBody({ mon, canWrite, onClose, rows, jobs, pick, setPick, jobPick, setJobPick, slot, setSlot, error, setError, notice, setNotice, busy, setBusy, reload }: {
  mon: string; canWrite: boolean; onClose: () => void; rows: GabulSmsRow[] | null; jobs: GabulSmsJob[];
  pick: Set<number>; setPick: Dispatch<SetStateAction<Set<number>>>; jobPick: Set<number>; setJobPick: Dispatch<SetStateAction<Set<number>>>;
  slot: { day: string; hm: string }; setSlot: Dispatch<SetStateAction<{ day: string; hm: string }>>;
  error: string | null; setError: (v: string | null) => void; notice: string | null; setNotice: (v: string | null) => void;
  busy: boolean; setBusy: (v: boolean) => void; reload: () => void;
}) {
  const jobBy = useMemo(() => {
    const m = new Map<number, GabulSmsJob>();
    for (const j of jobs) if (LIVE.has(j.status) && !m.has(j.owner_id)) m.set(j.owner_id, j);
    return m;
  }, [jobs]);
  const canPick = (r: GabulSmsRow) => r.ownerId != null && !!r.message && isMobile(r.phone) && !jobBy.has(r.ownerId);
  const pickable = (rows ?? []).filter(canPick);
  const lms = (rows ?? []).filter((r) => r.ownerId != null && pick.has(r.ownerId) && r.bytes > 90).length;
  const toggle = (id: number) => setPick((s) => { const x = new Set(s); if (x.has(id)) x.delete(id); else x.add(id); return x; });
  const reserve = async () => {
    const n = pick.size;
    if (!window.confirm(`${n}명에게 ${slot.day} ${slot.hm} 에 문자를 보냅니다.${lms ? `\n(그중 ${lms}명은 90바이트를 넘어 장문(LMS)으로 갑니다)` : ''}\n\n예약할까요? 보내기 전까지는 취소할 수 있습니다.`)) return;
    setBusy(true); setError(null); setNotice(null);
    try {
      const r = await api.reserveGabulSms({ mon, sendAt: `${slot.day} ${slot.hm}`, ownerIds: [...pick] });
      setNotice(`${r.reserved}명 예약했습니다 (${r.sendAt}).${r.skipped.length ? ` 빠진 ${r.skipped.length}명: ${r.skipped.map((s) => `${s.ownerName ?? s.ownerId}(${s.reason})`).join(', ')}` : ''}`);
      reload();
    } catch (e) { setError(errMsg(e)); } finally { setBusy(false); }
  };
  const cancel = async () => {
    if (!window.confirm(`고른 예약 ${jobPick.size}건을 취소할까요?`)) return;
    setBusy(true); setError(null); setNotice(null);
    try {
      const r = await api.cancelGabulSms({ ids: [...jobPick] });
      setNotice(`${r.cancelled}건 취소했습니다.`);
      reload();
    } catch (e) { setError(errMsg(e)); } finally { setBusy(false); }
  };
  const waiting = jobs.filter((j) => j.status === '대기');
  return (
    <div className="card space-y-3 p-4">
      <div className="flex flex-wrap items-center gap-2">
        <h2 className="text-base font-semibold">{mon} 부족분 문자</h2>
        <span className="text-xs text-ink-soft">고른 차주에게 고른 날짜·시각에 보냅니다(발신 1588-9718 · 10분마다 발송).</span>
        <button className="btn ml-auto" onClick={onClose}>닫기</button>
      </div>
      {error && <ErrorBox message={error} />}
      {notice && <div className="rounded bg-emerald-50 p-2 text-xs text-emerald-800">{notice}</div>}
      {canWrite && (
        <div className="flex flex-wrap items-center gap-2 rounded bg-canvas p-2 text-sm">
          <span>보낼 때</span>
          <input type="date" className="field w-auto" value={slot.day} onChange={(e) => setSlot((s) => ({ ...s, day: e.target.value }))} aria-label="보낼 날짜" />
          <select className="field w-auto" value={slot.hm} onChange={(e) => setSlot((s) => ({ ...s, hm: e.target.value }))} aria-label="보낼 시각">
            {TIMES.map((t) => <option key={t} value={t}>{t}</option>)}
          </select>
          <span className="text-xs text-ink-soft">{pick.size}명 고름{lms ? ` · 장문 ${lms}` : ''}</span>
          <button className="btn-primary ml-auto" disabled={busy || !pick.size || !slot.day} onClick={reserve}>{busy ? '처리 중…' : `${pick.size}명 예약`}</button>
        </div>
      )}
      <div className="max-h-[45vh] overflow-auto">
        <table className="w-full min-w-[48rem] text-xs tabular-nums [&_thead_th]:sticky [&_thead_th]:top-0 [&_thead_th]:bg-surface">
          <thead>
            <tr className="text-ink-soft">
              <th className="w-8 px-1 py-1">
                {canWrite && <input type="checkbox" aria-label="전체 고르기" checked={!!pickable.length && pickable.every((r) => pick.has(r.ownerId!))}
                                    onChange={(e) => setPick(e.target.checked ? new Set(pickable.map((r) => r.ownerId!)) : new Set())} />}
              </th>
              <th className="px-1 py-1 text-left">차주</th><th className="px-1 py-1 text-left">번호</th>
              <th className="px-1 py-1 text-left">문구</th><th className="px-1 py-1 text-right">바이트</th><th className="px-1 py-1 text-left">예약</th>
            </tr>
          </thead>
          <tbody>
            {rows === null && <tr><td colSpan={6} className="px-1 py-4 text-center text-ink-faint">불러오는 중…</td></tr>}
            {(rows ?? []).map((r, i) => {
              const job = r.ownerId != null ? jobBy.get(r.ownerId) : undefined;
              const why = !r.message ? '그 달 차인지급액 없음' : !isMobile(r.phone) ? '휴대폰 번호 없음' : null;
              return (
                <tr key={r.ownerId ?? `x${i}`} className={`border-t border-line ${why ? 'text-ink-faint' : ''}`}>
                  <td className="px-1 py-1 text-center">
                    {canWrite && <input type="checkbox" aria-label={`${r.ownerName} 고르기`} disabled={!canPick(r)} checked={r.ownerId != null && pick.has(r.ownerId)} onChange={() => r.ownerId != null && toggle(r.ownerId)} />}
                  </td>
                  <td className="px-1 py-1">{r.ownerName}</td>
                  <td className="px-1 py-1">{r.phone ?? '—'}</td>
                  <td className="px-1 py-1">{r.message ?? <span className="text-ink-faint">{why}</span>}</td>
                  <td className={`px-1 py-1 text-right ${r.bytes > 90 ? 'text-amber-700' : ''}`}>{r.message ? `${r.bytes}${r.bytes > 90 ? ' 장문' : ''}` : ''}</td>
                  <td className="px-1 py-1">{job ? <span className={`rounded px-1.5 ${JOB_CLS[job.status]}`}>{job.status} {job.status === '보냄' ? job.sent_at : job.send_at}</span> : why ? <span>{why}</span> : ''}</td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
      <div>
        <div className="mb-1 flex items-center gap-2">
          <h3 className="text-sm font-semibold">예약·발송 기록</h3>
          <span className="text-xs text-ink-soft">대기 {waiting.length} · 보냄 {jobs.filter((j) => j.status === '보냄').length} · 실패 {jobs.filter((j) => j.status === '실패').length}</span>
          {canWrite && <button className="btn ml-auto" disabled={busy || !jobPick.size} onClick={cancel}>고른 예약 취소 ({jobPick.size})</button>}
        </div>
        <div className="max-h-[30vh] overflow-auto">
          <table className="w-full min-w-[40rem] text-xs tabular-nums [&_thead_th]:sticky [&_thead_th]:top-0 [&_thead_th]:bg-surface">
            <thead><tr className="text-ink-soft"><th className="w-8" /><th className="px-1 py-1 text-left">예약 시각</th><th className="px-1 py-1 text-left">차주</th><th className="px-1 py-1 text-left">번호</th><th className="px-1 py-1 text-left">상태</th><th className="px-1 py-1 text-left">예약한 사람</th></tr></thead>
            <tbody>
              {jobs.map((j) => (
                <tr key={j.id} className="border-t border-line" title={j.message}>
                  <td className="px-1 py-1 text-center">
                    {canWrite && j.status === '대기' && <input type="checkbox" aria-label={`${j.owner_name} 예약 고르기`} checked={jobPick.has(j.id)}
                      onChange={() => setJobPick((s) => { const x = new Set(s); if (x.has(j.id)) x.delete(j.id); else x.add(j.id); return x; })} />}
                  </td>
                  <td className="px-1 py-1">{j.send_at}</td>
                  <td className="px-1 py-1">{j.owner_name}</td>
                  <td className="px-1 py-1">{j.phone}</td>
                  <td className="px-1 py-1"><span className={`rounded px-1.5 ${JOB_CLS[j.status]}`}>{j.status}</span>{j.sent_at && <span className="ml-1 text-ink-soft">{j.sent_at}</span>}{j.error && <span className="ml-1 text-red-700">{j.error}</span>}{j.cancelled_by && <span className="ml-1 text-ink-faint">{j.cancelled_by.split('@')[0]}</span>}</td>
                  <td className="px-1 py-1 text-ink-soft">{j.created_by?.split('@')[0]}</td>
                </tr>
              ))}
              {!jobs.length && <tr><td colSpan={6} className="px-1 py-3 text-center text-ink-faint">아직 예약이 없습니다.</td></tr>}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}
